import { memo, useEffect, useState, useSyncExternalStore, type CSSProperties } from 'react'
import type { SlideshowTransition } from '../../lib/infoserviceSlideshowApi.js'
import {
  PREVIEW_HOLD_MS,
  previewStepMs,
  previewStillMs,
  transitionPreviewSpec,
  type TransitionPreviewSpec,
} from '../../lib/transitionPreview.js'

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

function subscribeReducedMotion(onChange: () => void): () => void {
  const query = window.matchMedia?.(REDUCED_MOTION)
  query?.addEventListener('change', onChange)
  return () => query?.removeEventListener('change', onChange)
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.(REDUCED_MOTION).matches ?? false
}

/** Une animation : en lecture après la pose, ou figée à l'instant `stillMs`. */
function layerAnimation(name: string, durationMs: number, startMs: number, animate: boolean, stillMs: number, fill: string, timing = 'linear'): string {
  const delayMs = animate ? PREVIEW_HOLD_MS + startMs : startMs - stillMs
  return `${name} ${durationMs}ms ${timing} ${delayMs}ms ${fill}`
}

function layerStyle(animation: string, animate: boolean): CSSProperties {
  return { animation, animationPlayState: animate ? 'running' : 'paused' }
}

const PreviewImage = memo(function PreviewImage({ name, style }: { name: 'A' | 'B'; style?: CSSProperties }) {
  return (
    <span className={`transition-preview__image is-${name.toLowerCase()}`} style={style}>
      <span className="transition-preview__letter">{name}</span>
      <span className="transition-preview__lines">
        <span />
        <span />
      </span>
    </span>
  )
})

function specSignature(spec: TransitionPreviewSpec): string {
  return `${spec.incoming}|${spec.outgoing ?? ''}|${spec.durationMs}|${spec.cutMs ?? ''}|${spec.timing}`
}

/**
 * Vignette 16:9 qui montre une transition avec deux images factices, A et B.
 * En lecture, elle boucle à la vraie durée réglée ; à l'arrêt (ou si le poste
 * demande moins d'animations), elle reste figée au milieu du mouvement.
 */
export const TransitionPreview = memo(function TransitionPreview({ transition, playing }: {
  transition: SlideshowTransition
  playing: boolean
}) {
  const spec = transitionPreviewSpec(transition)
  const reducedMotion = useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => false)
  const animate = playing && !reducedMotion
  const signature = specSignature(spec)
  const stepMs = previewStepMs(spec)
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (!animate) return undefined
    const timer = window.setInterval(() => setStep(current => current + 1), stepMs)
    return () => window.clearInterval(timer)
  }, [animate, stepMs, signature])

  // Étape paire : A part, B arrive ; étape impaire : l'inverse.
  const [outgoing, incoming]: ['A' | 'B', 'A' | 'B'] = step % 2 === 0 || !animate ? ['A', 'B'] : ['B', 'A']
  const stillMs = previewStillMs(spec)
  const isStinger = spec.cutMs !== null
  const cutMs = spec.cutMs ?? 0
  const key = `${step}:${animate}:${signature}`

  const incomingStyle = layerStyle(isStinger
    ? layerAnimation(spec.incoming, 1, cutMs, animate, stillMs, 'both')
    : layerAnimation(spec.incoming, spec.durationMs, 0, animate, stillMs, 'both', spec.timing), animate)
  const outgoingStyle = spec.outgoing
    ? layerStyle(layerAnimation(spec.outgoing, spec.durationMs, 0, animate, stillMs, 'both', spec.timing), animate)
    : undefined
  const stingerStyle = isStinger
    ? {
        animation: [
          // Schéma du stinger : sa vraie courbe est dans la vidéo, pas dans CasparCG.
          layerAnimation('slideshow-preview-band-in', Math.max(1, cutMs), 0, animate, stillMs, 'both', 'var(--ease-in-out)'),
          layerAnimation('slideshow-preview-band-out', Math.max(1, spec.durationMs - cutMs), cutMs, animate, stillMs, 'forwards', 'var(--ease-in-out)'),
        ].join(', '),
        animationPlayState: animate ? 'running, running' : 'paused, paused',
      }
    : undefined

  return (
    <span className="transition-preview" aria-hidden="true" key={key}>
      <PreviewImage name={outgoing} style={outgoingStyle} />
      <PreviewImage name={incoming} style={incomingStyle} />
      {stingerStyle && (
        <span className="transition-preview__stinger" style={stingerStyle}>Stinger</span>
      )}
    </span>
  )
})
